/**
 * The screenshot tour: real frames of the product, captured by `pnpm tour` from a simulated session
 * (see scripts/tour.mjs) and served from /tour/. The manifest carries each frame's pixel size so the
 * page reserves the space before the image arrives, and its surface so the layout can treat a
 * projector frame, a phone frame and a document differently.
 *
 * Captions live in the locale files as `tour.<id>.title` / `tour.<id>.body`.
 */
export type TourSurface = "screen" | "phone" | "desktop" | "report";

export interface TourFrame {
  id: string;
  src: string;
  width: number;
  height: number;
  surface: TourSurface;
}

/** Frame order is the order of a session: the room fills, the call comes in, the vote, the reveal, the debrief, the report. */
export const TOUR_FRAMES: readonly TourFrame[] = [
  { id: "screen-lobby", src: "/tour/01-screen-lobby.webp", width: 1600, height: 900, surface: "screen" },
  { id: "phone-join", src: "/tour/02-phone-join.webp", width: 780, height: 1688, surface: "phone" },
  { id: "screen-call", src: "/tour/03-screen-call.webp", width: 1600, height: 900, surface: "screen" },
  { id: "phone-vote", src: "/tour/04-phone-vote.webp", width: 780, height: 1688, surface: "phone" },
  { id: "console", src: "/tour/05-console.webp", width: 1440, height: 900, surface: "desktop" },
  { id: "screen-reveal", src: "/tour/06-screen-reveal.webp", width: 1600, height: 900, surface: "screen" },
  { id: "phone-result", src: "/tour/07-phone-result.webp", width: 780, height: 1688, surface: "phone" },
  { id: "screen-debrief", src: "/tour/08-screen-debrief.webp", width: 1600, height: 900, surface: "screen" },
  { id: "report", src: "/tour/09-report.webp", width: 1280, height: 1800, surface: "report" },
  { id: "phone-rtl", src: "/tour/10-phone-rtl.webp", width: 780, height: 1688, surface: "phone" },
];

/** The three frames the landing page shows before inviting the reader to the full tour. */
export const TOUR_STRIP: readonly string[] = ["screen-call", "phone-vote", "report"];

export function tourFrame(id: string): TourFrame {
  const frame = TOUR_FRAMES.find((f) => f.id === id);
  if (!frame) throw new Error(`Unknown tour frame "${id}"`);
  return frame;
}
